import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

import { doc, cursor, scrl, getPosition, inViewport, registeredLoops, lenisScrollTo, stopLenis, startLenis, touchDevice } from './../../js/utils/events.js';
import { map, clamp } from './../../js/utils/math.js';

gsap.registerPlugin(ScrollTrigger);

export default class Flipbook {
  init(root) {
    this.root = root;
    this.video = this.root.querySelector('.flipbook-video');
    this.btnWrapper = document.querySelector('.flipbook-controls');
    this.btn = this.btnWrapper.querySelector('.toggle-animations');
    this.animationPaused = false;
    this.checkVideoTime = this.checkVideoTime.bind(this);
    this.firstFrameImg = this.root.querySelector('.flipbook-first-frame');

    this.triggers = document.querySelectorAll('.flipbooktrigger');

    let currentSize = window.innerWidth > 1200 ? 'large' : 'small';

    // Set initial video
    this.video.src = currentSize === 'large' ? './assets/videos/tinkertank-notransparency-large-opt.mp4' : './assets/videos/tinkertank-notransparency-small-opt.mp4';
    this.video.load();

    window.addEventListener('resize', () => {
        if (doc.w > 1200 && currentSize === 'small') {
            // Upgrade from small to large
            this.video.src = './assets/videos/tinkertank-notransparency-large-opt.mp4';
            this.video.load();
            currentSize = 'large';
        }
    });
    

    this.siblingColumns = [...this.triggers, this.root].map(trigger => {
      const parentColumn = trigger.closest('.column');
      return Array.from(parentColumn.parentElement.children).filter(
        sibling => sibling !== parentColumn && sibling.classList.contains('column')
      );
    });

    this.siblingColumns.forEach((siblingColumn, index) => {
      siblingColumn[0].classList.add('flipbook-sibling-column');
    });

    this.videoDuration = 14.2; // Default duration, will be updated when video metadata is loaded
    this.video.addEventListener('loadedmetadata', () => {
      this.videoDuration = this.video.duration;
    //   console.log('video loaded', this.videoDuration);
    });

    setTimeout(() => {
      this.setupScrollTriggers();
    }, 200);

    this.btn.addEventListener('click', () => {
      this.animationPaused = !this.animationPaused;
      this.btn.classList.toggle('is-paused', this.animationPaused);
      
      if(this.animationPaused) {
          this.video.pause();
      } else {
          this.video.play();
          requestAnimationFrame(this.checkVideoTime);
        //   console.log('play it')
      }
    });
  }

  setupScrollTriggers() {
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());

    const container = this.root.closest('.column');
    container.classList.add('no-container');

    const startPinPos = window.innerWidth < 720 ? 'bottom bottom' : 'center center';

    // Pin the video
    ScrollTrigger.create({
      trigger: this.root,
      start: startPinPos,
      endTrigger: this.triggers[this.triggers.length - 1],
      end: 'center center',
    //   markers: true,
      pin: this.root,
      onEnter: () => {
        const layoutLeft = document.querySelector('.layout').getBoundingClientRect().left;
        Object.assign(this.btnWrapper.style, {
          position: 'fixed',
          left: `${layoutLeft}px`,
          opacity: '1',
          transform: 'translateY(0)',
        });
      },
      onLeave: () => {
        this.btnWrapper.style.opacity = '0';
      },
      onEnterBack: () => {
        const layoutLeft = document.querySelector('.layout').getBoundingClientRect().left;
        Object.assign(this.btnWrapper.style, {
          position: 'fixed',
          left: `${layoutLeft}px`,
          opacity: '1',
          transform: 'translateY(0)',
        });
      },
      onLeaveBack: () => {
        this.btnWrapper.style.opacity = '0';
      }
    });

    this.desiredTime = 0;

    // Scroll scrubbing for each trigger section
    this.triggers.forEach((trigger, index) => {
        const startFrame = parseInt(trigger.dataset.startFrame, 10) - 1;
        const endFrame = parseInt(trigger.dataset.endFrame, 10) - 1;
    
        // Automatically find highest endFrame to calculate totalFrames
        const totalFrames = Math.max(
        ...Array.from(this.triggers).map(t => parseInt(t.dataset.endFrame, 10))
        );

        let startPos = 'top bottom';
        if(index === 0 && doc.w > 720) {
          startPos = 'top center';
        }
    
        ScrollTrigger.create({
        trigger: trigger,
        // start: index === 0 ? 'top center' : 'top bottom',
        // start: index === 0 && doc.w < 720 ? 'top center' : 'top bottom',
        start: startPos,
        end: 'bottom bottom',
        scrub: 1, // keep this to make ScrollTrigger update continuously
        // markers: true,
        onToggle: (self) => {
            const siblingColumn = this.siblingColumns[index];
            if (siblingColumn) {
            siblingColumn[0].classList.toggle('is-active', self.isActive);
            }
        },
        onUpdate: (self) => {
            const progressStart = startFrame / totalFrames;
            const progressEnd = endFrame / totalFrames;
            const videoProgress = map(self.progress, 0, 1, progressStart, progressEnd);
            this.desiredTime = clamp(videoProgress * this.videoDuration, 0, this.videoDuration);

            if (!this.animationPaused) {
                // console.log(this.desiredTime)

                if (this.video.currentTime < this.desiredTime && this.desiredTime - this.video.currentTime < 2.5) {
                    
                    this.video.play();
                    requestAnimationFrame(this.checkVideoTime);
                    // console.log('play it until desired time')
                } else {
                    this.video.pause();
                    this.video.currentTime = this.desiredTime;
                    // console.log('set time directly')
                }
            } else {
                this.video.pause();
            }

            if (this.desiredTime === 0) {
                this.firstFrameImg.style.display = 'block';
                this.video.style.display = 'none';
                this.root.style.zIndex = "0";
                this.video.currentTime = 0;
                this.video.pause();
            } else {
                this.video.style.display = 'block';
                this.root.style.zIndex = "-1";
                this.firstFrameImg.style.display = 'none';
            }
        }
        });
    });

    // Start the loop
    requestAnimationFrame(this.checkVideoTime);
  }

  checkVideoTime() {
    // console.log('checkVideoTime')
    // console.log(this.video)
    if (this.video && this.video.currentTime > this.desiredTime) {
        this.video.pause();
    } else {
        requestAnimationFrame(this.checkVideoTime);
    }
  }

}
